/**
 * Multi-account switcher — stores session tokens locally for quick account switching.
 * Links between accounts are bidirectional: unlinking A↔B hides each from the other.
 */

import { apiPath } from "../lib/apiPath";

export type SavedAccount = {
    id: string;
    email: string;
    displayName: string | null;
    sessionToken: string;
};

const KEY_SAVED_ACCOUNTS = "savedAccountsV1";
const KEY_ACCOUNT_LINKS = "savedAccountLinksV1";

type AccountLinks = Record<string, string[]>;

function readSavedAccounts(): SavedAccount[] {
    try {
        const raw = localStorage.getItem(KEY_SAVED_ACCOUNTS);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        const out: SavedAccount[] = [];
        for (const row of parsed) {
            if (!row || typeof row !== "object") continue;
            const id = String((row as SavedAccount).id || "").trim();
            const email = String((row as SavedAccount).email || "").trim();
            const sessionToken = String((row as SavedAccount).sessionToken || "").trim();
            const displayName =
                (row as SavedAccount).displayName == null
                    ? null
                    : String((row as SavedAccount).displayName).trim() || null;
            if (!id || !email || sessionToken.length < 16) continue;
            out.push({ id, email, displayName, sessionToken });
        }
        return out;
    } catch {
        return [];
    }
}

function writeSavedAccounts(list: SavedAccount[]) {
    localStorage.setItem(KEY_SAVED_ACCOUNTS, JSON.stringify(list));
}

function readAccountLinks(): AccountLinks {
    try {
        const raw = localStorage.getItem(KEY_ACCOUNT_LINKS);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
        const out: AccountLinks = {};
        for (const [userId, peers] of Object.entries(parsed as Record<string, unknown>)) {
            const id = String(userId || "").trim();
            if (!id || !Array.isArray(peers)) continue;
            const cleaned = [
                ...new Set(
                    peers
                        .map((p) => String(p || "").trim())
                        .filter((p) => p && p !== id),
                ),
            ];
            if (cleaned.length) out[id] = cleaned;
        }
        return out;
    } catch {
        return {};
    }
}

function writeAccountLinks(links: AccountLinks) {
    localStorage.setItem(KEY_ACCOUNT_LINKS, JSON.stringify(links));
}

/** Older installs only had a flat list — treat every pair as linked once. */
function migrateLegacyFullyConnectedLinks(): void {
    const accounts = readSavedAccounts();
    if (accounts.length < 2) return;
    const links = readAccountLinks();
    if (Object.keys(links).length > 0) return;
    const next: AccountLinks = {};
    for (const a of accounts) {
        next[a.id] = accounts.filter((b) => b.id !== a.id).map((b) => b.id);
    }
    writeAccountLinks(next);
}

function addBidirectionalLink(aId: string, bId: string): void {
    const a = String(aId || "").trim();
    const b = String(bId || "").trim();
    if (!a || !b || a === b) return;
    const links = readAccountLinks();
    const peersA = new Set(links[a] || []);
    const peersB = new Set(links[b] || []);
    peersA.add(b);
    peersB.add(a);
    links[a] = [...peersA];
    links[b] = [...peersB];
    writeAccountLinks(links);
}

function removeBidirectionalLink(aId: string, bId: string): void {
    const a = String(aId || "").trim();
    const b = String(bId || "").trim();
    if (!a || !b) return;
    const links = readAccountLinks();
    if (links[a]) {
        links[a] = links[a].filter((id) => id !== b);
        if (!links[a].length) delete links[a];
    }
    if (links[b]) {
        links[b] = links[b].filter((id) => id !== a);
        if (!links[b].length) delete links[b];
    }
    writeAccountLinks(links);
}

function unlinkUserFromAll(userId: string): void {
    const id = String(userId || "").trim();
    if (!id) return;
    const links = readAccountLinks();
    const peers = [...(links[id] || [])];
    for (const peer of peers) removeBidirectionalLink(id, peer);
    if (links[id]) {
        delete links[id];
        writeAccountLinks(links);
    }
}

function getLinkedPeerIds(userId: string): string[] {
    migrateLegacyFullyConnectedLinks();
    const id = String(userId || "").trim();
    if (!id) return [];
    return [...(readAccountLinks()[id] || [])];
}

export function getSavedAccounts(): SavedAccount[] {
    return readSavedAccounts();
}

export type RememberAccountOpts = {
    /**
     * When true, insert the account into Switch Account if missing.
     * Default false: only refresh an already-linked entry (normal sign-in must not grow the list).
     */
    addIfMissing?: boolean;
};

export function rememberAccountFromAuth(
    user: { id: string; email?: string | null; displayName?: string | null },
    sessionToken: string | null | undefined,
    opts?: RememberAccountOpts,
): SavedAccount | null {
    const token = String(sessionToken || "").trim();
    if (!user?.id || token.length < 16) return null;
    const email = String(user.email || "").trim();
    if (!email) return null;
    const entry: SavedAccount = {
        id: user.id,
        email,
        displayName: user.displayName != null ? String(user.displayName).trim() || null : null,
        sessionToken: token,
    };
    const existing = readSavedAccounts();
    const already = existing.some((a) => a.id === entry.id);
    if (!already && !opts?.addIfMissing) return null;
    const next = [entry, ...existing.filter((a) => a.id !== entry.id)].slice(0, 12);
    writeSavedAccounts(next);
    return entry;
}

/** Keep both accounts in the switcher list and link them both ways (e.g. after Add Account). */
export function linkSavedAccounts(a: SavedAccount | null, b: SavedAccount | null): void {
    if (a) rememberAccountFromAuth(a, a.sessionToken, { addIfMissing: true });
    if (b) rememberAccountFromAuth(b, b.sessionToken, { addIfMissing: true });
    if (a && b) addBidirectionalLink(a.id, b.id);
}

let addAccountReturnTarget: SavedAccount | null = null;

export function getAddAccountReturnTarget(): SavedAccount | null {
    return addAccountReturnTarget;
}

export function clearAddAccountReturnTarget(): void {
    addAccountReturnTarget = null;
}

export function beginAddAccountFlow(returnAccount: SavedAccount | null): boolean {
    if (!returnAccount?.sessionToken || returnAccount.sessionToken.length < 16) return false;
    addAccountReturnTarget = returnAccount;
    rememberAccountFromAuth(returnAccount, returnAccount.sessionToken, { addIfMissing: true });
    return true;
}

export async function finishAddAccountFlow(
    addedAccount: SavedAccount,
): Promise<{ ok: boolean; error?: string }> {
    const returnTo = addAccountReturnTarget;
    linkSavedAccounts(returnTo, addedAccount);
    addAccountReturnTarget = null;
    if (!returnTo || returnTo.id === addedAccount.id) return { ok: true };
    return switchToSavedAccount(returnTo);
}

export async function cancelAddAccountFlow(): Promise<{ ok: boolean; error?: string }> {
    const returnTo = addAccountReturnTarget;
    addAccountReturnTarget = null;
    if (!returnTo) return { ok: true };
    return switchToSavedAccount(returnTo);
}

export function removeSavedAccount(userId: string): void {
    const id = String(userId || "").trim();
    if (!id) return;
    unlinkUserFromAll(id);
    writeSavedAccounts(readSavedAccounts().filter((a) => a.id !== id));
}

/**
 * Remove `targetUserId` from Switch Account for the current user.
 * Unlinks both directions so neither account lists the other.
 */
export function unlinkSavedAccount(currentUserId: string | null, targetUserId: string): void {
    const target = String(targetUserId || "").trim();
    const current = String(currentUserId || "").trim();
    if (!target) return;
    if (current && current !== target) {
        removeBidirectionalLink(current, target);
    } else {
        unlinkUserFromAll(target);
    }
    writeSavedAccounts(readSavedAccounts().filter((a) => a.id !== target));
}

/** Drop every saved Switch Account entry for this user (e.g. after logout on all devices). */
export function purgeSavedAccountEverywhere(userId: string): void {
    removeSavedAccount(userId);
}

export async function switchToSavedAccount(account: SavedAccount): Promise<{ ok: boolean; error?: string }> {
    try {
        const r = await fetch(apiPath("/api/switch-account"), {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionToken: account.sessionToken }),
        });
        const data = (await r.json().catch(() => ({}))) as {
            ok?: boolean;
            sessionToken?: string;
            user?: { id: string; email?: string; displayName?: string | null };
            message?: string;
            error?: string;
        };
        if (!r.ok) {
            if (r.status === 401) removeSavedAccount(account.id);
            return { ok: false, error: data.message || data.error || "Could not switch account." };
        }
        if (data.user && data.sessionToken) {
            rememberAccountFromAuth(data.user, data.sessionToken);
        }
        return { ok: true };
    } catch {
        return { ok: false, error: "Network error." };
    }
}

let menuMounted = false;
let menuEl: HTMLElement | null = null;
let anchorBtn: HTMLButtonElement | null = null;
let onAddAccount: (() => void) | null = null;
let onSwitchAccount: ((account: SavedAccount) => void | Promise<void>) | null = null;
let onBeforeSwitch: (() => void | Promise<void>) | null = null;
let onSwitched: (() => void | Promise<void>) | null = null;

function hideSwitchMenu() {
    menuEl?.classList.add("hidden");
}

function accountLabel(a: SavedAccount): string {
    return a.displayName || a.email || a.id;
}

const TRASH_ICON_SVG =
    '<svg class="accountSwitchTrashIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9zm-1 12h12V8H6v13z"/></svg>';

function accountsVisibleForUser(currentUserId: string | null): SavedAccount[] {
    migrateLegacyFullyConnectedLinks();
    const all = readSavedAccounts();
    if (!currentUserId) return all;
    const peerIds = new Set(getLinkedPeerIds(currentUserId));
    const byId = new Map(all.map((a) => [a.id, a]));
    const out: SavedAccount[] = [];
    const self = byId.get(currentUserId);
    if (self) out.push(self);
    for (const peerId of peerIds) {
        const peer = byId.get(peerId);
        if (peer && peer.id !== currentUserId) out.push(peer);
    }
    return out;
}

function showSwitchMenu(btn: HTMLButtonElement, currentUserId: string | null) {
    if (!menuEl) return;
    const accounts = accountsVisibleForUser(currentUserId);
    menuEl.replaceChildren();

    const list = document.createElement("div");
    list.className = "accountSwitchList";

    for (const acc of accounts) {
        const row = document.createElement("div");
        row.className = "accountSwitchRow";

        const item = document.createElement("button");
        item.type = "button";
        item.className = "accountSwitchItem";
        if (acc.id === currentUserId) item.classList.add("accountSwitchItem--active");
        item.textContent = accountLabel(acc);
        item.setAttribute("role", "menuitem");
        item.onclick = () => {
            hideSwitchMenu();
            if (acc.id === currentUserId) return;
            void (async () => {
                if (onSwitchAccount) {
                    await onSwitchAccount(acc);
                    return;
                }
                await onBeforeSwitch?.();
                const result = await switchToSavedAccount(acc);
                if (!result.ok) {
                    alert(result.error || "Could not switch account.");
                    return;
                }
                await onSwitched?.();
            })();
        };

        const trash = document.createElement("button");
        trash.type = "button";
        trash.className = "accountSwitchTrashBtn";
        trash.setAttribute("aria-label", `Remove ${accountLabel(acc)} from Switch Account`);
        trash.innerHTML = TRASH_ICON_SVG;
        trash.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            unlinkSavedAccount(currentUserId, acc.id);
            if (acc.id === currentUserId) {
                hideSwitchMenu();
                return;
            }
            showSwitchMenu(btn, currentUserId);
        };

        row.appendChild(item);
        row.appendChild(trash);
        list.appendChild(row);
    }

    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "accountSwitchItem accountSwitchItem--add";
    addBtn.textContent = "Add Account";
    addBtn.onclick = () => {
        hideSwitchMenu();
        onAddAccount?.();
    };

    menuEl.appendChild(list);
    menuEl.appendChild(addBtn);
    menuEl.classList.remove("hidden");

    const rect = btn.getBoundingClientRect();
    const pad = 8;
    let left = rect.right + 8;
    let top = rect.top;
    menuEl.style.left = `${left}px`;
    menuEl.style.top = `${top}px`;
    const menuRect = menuEl.getBoundingClientRect();
    if (left + menuRect.width > window.innerWidth - pad) {
        left = Math.max(pad, rect.left - menuRect.width - 8);
        menuEl.style.left = `${left}px`;
    }
    if (top + menuRect.height > window.innerHeight - pad) {
        top = Math.max(pad, window.innerHeight - menuRect.height - pad);
        menuEl.style.top = `${top}px`;
    }
}

export type MountAccountSwitcherOpts = {
    switchBtn: HTMLButtonElement;
    getCurrentUserId: () => string | null;
    onAddAccount: () => void;
    /** Preferred: full switch flow (loading, local sign-out, session swap, data pull). */
    onSwitchAccount?: (account: SavedAccount) => void | Promise<void>;
    onBeforeSwitch?: () => void | Promise<void>;
    onSwitched?: () => void | Promise<void>;
};

export function mountAccountSwitcher(opts: MountAccountSwitcherOpts): void {
    anchorBtn = opts.switchBtn;
    onAddAccount = opts.onAddAccount;
    onSwitchAccount = opts.onSwitchAccount ?? null;
    onBeforeSwitch = opts.onBeforeSwitch ?? null;
    onSwitched = opts.onSwitched ?? null;

    if (!menuEl) {
        menuEl = document.createElement("div");
        menuEl.id = "accountSwitchMenu";
        menuEl.className = "accountSwitchMenu hidden";
        menuEl.setAttribute("role", "menu");
        document.body.appendChild(menuEl);
    }

    if (menuMounted) return;
    menuMounted = true;

    opts.switchBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (menuEl?.classList.contains("hidden")) {
            showSwitchMenu(opts.switchBtn, opts.getCurrentUserId());
        } else {
            hideSwitchMenu();
        }
    });

    document.addEventListener(
        "click",
        (e) => {
            if (!(e.target instanceof Element)) return;
            if (e.target.closest("#accountSwitchMenu") || e.target.closest("#sideSwitchAccountBtn")) return;
            hideSwitchMenu();
        },
        true,
    );

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") hideSwitchMenu();
    });
}

export function setSwitchAccountVisible(visible: boolean): void {
    if (anchorBtn) anchorBtn.classList.toggle("hidden", !visible);
    if (!visible) hideSwitchMenu();
}
